import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaBoxOpen, FaCheckCircle, FaRegStar, FaStar, FaTruck } from "react-icons/fa";
import useAuth from "../hooks/useAuth";
import { API_BASE } from "../config/api";
import { formatMaterialDisplayLabel } from "../utils/materialLabels";
import "./ReviewSubmissionPage.css";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const formatDate = (value) => {
  if (!value) {
    return "--";
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return "--";
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsedDate);
};

const renderStars = (filledCount, size = 18) =>
  [1, 2, 3, 4, 5].map((star) =>
    star <= filledCount ? (
      <FaStar key={star} size={size} />
    ) : (
      <FaRegStar key={star} size={size} />
    )
  );

const ReviewSubmissionPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [reviewContext, setReviewContext] = useState(null);
  const [submittedReview, setSubmittedReview] = useState(null);

  const orderId = searchParams.get("orderId") || "";
  const productId = searchParams.get("productId") || "";

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login", {
        replace: true,
        state: { from: `${location.pathname}${location.search}` },
      });
    }
  }, [authLoading, user, navigate, location.pathname, location.search]);

  useEffect(() => {
    const fetchReviewContext = async () => {
      if (authLoading || !user) {
        return;
      }

      if (!orderId || !productId) {
        setError("The review link is incomplete. Please open the link again from your delivery email.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const response = await axios.get(`${API_BASE}/reviews/review-context`, {
          params: { orderId, productId },
          withCredentials: true,
        });

        const nextContext = response.data?.reviewContext || null;
        setReviewContext(nextContext);

        if (nextContext?.existingReview) {
          setSubmittedReview(nextContext.existingReview);
          setRating(Number(nextContext.existingReview.rating) || 0);
          setFeedback(nextContext.existingReview.feedback || "");
        } else {
          setSubmittedReview(null);
          setRating(0);
          setFeedback("");
        }
      } catch (requestError) {
        setError(
          requestError.response?.data?.message ||
            "We could not load this review page. Please try again from your account."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchReviewContext();
  }, [authLoading, user, orderId, productId]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!rating) {
      setError("Please choose a rating before submitting your review.");
      return;
    }

    if (!feedback.trim()) {
      setError("Please share a short review before submitting.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const response = await axios.post(
        `${API_BASE}/reviews/submit`,
        {
          orderId,
          productId,
          rating,
          feedback: feedback.trim(),
        },
        { withCredentials: true }
      );

      const createdReview = response.data?.review || {
        rating,
        feedback: feedback.trim(),
        createdAt: new Date().toISOString(),
      };

      setSubmittedReview(createdReview);
      setReviewContext((currentContext) =>
        currentContext
          ? {
              ...currentContext,
              existingReview: createdReview,
            }
          : currentContext
      );

      window.dispatchEvent(new Event("refreshProductReviews"));
    } catch (submitError) {
      setError(
        submitError.response?.data?.message ||
          "Your review could not be submitted right now. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const productImage = useMemo(() => {
    if (reviewContext?.orderItem?.image) {
      return reviewContext.orderItem.image;
    }

    return reviewContext?.product?.images?.[0] || "";
  }, [reviewContext]);

  if (authLoading || loading) {
    return (
      <div className="review-page review-page--loading">
        <div className="review-loading-card">
          <div className="review-loading-spinner" />
          <h2>Preparing your review page</h2>
          <p>We are loading your delivered order and product details.</p>
        </div>
      </div>
    );
  }

  if (error && !reviewContext) {
    return (
      <div className="review-page review-page--loading">
        <div className="review-loading-card review-loading-card--error">
          <h2>Review page unavailable</h2>
          <p>{error}</p>
          <div className="review-loading-actions">
            <Link to="/order" className="review-secondary-link">
              View my orders
            </Link>
            <Link to="/store" className="review-primary-link">
              Continue shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const product = reviewContext?.product || {};
  const order = reviewContext?.order || {};
  const orderItem = reviewContext?.orderItem || {};
  const submittedAt = submittedReview?.createdAt ? formatDate(submittedReview.createdAt) : "";

  return (
    <div className="review-page">
      <div className="review-page-shell">
        <div className="review-page-topbar">
          <button
            type="button"
            className="review-back-button"
            onClick={() => navigate(`/order/${order._id}`)}
          >
            <FaArrowLeft />
            Back to Order
          </button>
          <div className="review-topbar-note">
            <span>Delivered feedback</span>
            <strong>Share your experience with Panchali Jewels</strong>
          </div>
        </div>

        <div className="review-page-layout">
          <aside className="review-product-card">
            <div className="review-product-media">
              {productImage ? <img src={productImage} alt={product.title || orderItem.title || "Product"} /> : null}
            </div>

            <div className="review-product-body">
              <div className="review-product-badge">
                <FaTruck />
                Delivered on {formatDate(order.deliveredAt)}
              </div>
              <h1>{product.title || orderItem.title || "Product Review"}</h1>
              <p className="review-product-subtitle">
                Help future customers by rating your purchase and sharing what you liked most.
              </p>

              <div className="review-meta-grid">
                <div className="review-meta-card">
                  <span>Order</span>
                  <strong>{order._id ? String(order._id).slice(-8).toUpperCase() : "--"}</strong>
                </div>
                <div className="review-meta-card">
                  <span>Paid</span>
                  <strong>{formatCurrency(order.totalPrice)}</strong>
                </div>
                <div className="review-meta-card">
                  <span>Quantity</span>
                  <strong>{orderItem.quantity || 1}</strong>
                </div>
                <div className="review-meta-card">
                  <span>Size</span>
                  <strong>{orderItem.selectedSize || "Standard"}</strong>
                </div>
              </div>

              <div className="review-product-footer">
                <div className="review-product-footer-row">
                  <span>Category</span>
                  <strong>{product.type || "Jewelry"}</strong>
                </div>
                <div className="review-product-footer-row">
                  <span>Material</span>
                  <strong>{formatMaterialDisplayLabel(product.material) || "Panchali Selection"}</strong>
                </div>
                <div className="review-product-footer-row">
                  <span>Product price</span>
                  <strong>{formatCurrency(orderItem.price || product.offerPrice || product.price)}</strong>
                </div>
              </div>
            </div>
          </aside>

          <section className="review-form-card">
            <div className="review-form-header">
              <span className="review-form-kicker">Customer Review</span>
              <h2>{submittedReview ? "Thank you for your review" : "Rate your delivered product"}</h2>
              <p>
                {submittedReview
                  ? "Your feedback has been recorded and will appear publicly after admin approval."
                  : "Give a star rating and write a quick comment about the product you received."}
              </p>
            </div>

            {submittedReview ? (
              <div className="review-success-panel">
                <div className="review-success-badge">
                  <FaCheckCircle />
                  Review submitted for approval
                </div>

                <div className="review-success-stars">{renderStars(Number(submittedReview.rating) || 0, 22)}</div>

                <div className="review-success-copy">
                  <h3>{submittedAt ? `Submitted on ${submittedAt}` : "Review saved successfully"}</h3>
                  <p>{submittedReview.feedback}</p>
                </div>

                <div className="review-success-actions">
                  <button
                    type="button"
                    className="review-primary-link review-primary-button"
                    onClick={() => navigate(`/product/${productId}`, { state: { refreshReviews: true } })}
                  >
                    View product page
                  </button>
                  <button
                    type="button"
                    className="review-secondary-link review-secondary-button"
                    onClick={() => navigate(`/order/${order._id}`)}
                  >
                    Back to order
                  </button>
                </div>
              </div>
            ) : (
              <form className="review-form" onSubmit={handleSubmit}>
                <div className="review-rating-block">
                  <span className="review-field-label">Your rating</span>
                  <div className="review-star-picker" role="radiogroup" aria-label="Product rating">
                    {[1, 2, 3, 4, 5].map((star) => {
                      const isActive = star <= rating;
                      return (
                        <button
                          key={star}
                          type="button"
                          className={`review-star-button ${isActive ? "is-active" : ""}`}
                          onClick={() => setRating(star)}
                          aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
                        >
                          <FaStar />
                        </button>
                      );
                    })}
                  </div>
                  <p className="review-field-help">
                    {rating
                      ? `${rating} out of 5 selected`
                      : "Tap the stars to rate the product from 1 to 5."}
                  </p>
                </div>

                <label className="review-textarea-field">
                  <span className="review-field-label">Write your review</span>
                  <textarea
                    rows={7}
                    maxLength={500}
                    value={feedback}
                    onChange={(event) => setFeedback(event.target.value)}
                    placeholder="Tell other customers about the quality, finish, fit, packaging, or your overall experience."
                  />
                  <span className="review-character-count">{feedback.trim().length}/500</span>
                </label>

                {error ? <div className="review-error-banner">{error}</div> : null}

                <div className="review-form-notes">
                  <div className="review-note-card">
                    <FaBoxOpen />
                    <div>
                      <strong>One review per delivered product</strong>
                      <p>This keeps customer feedback genuine and tied to a real purchase.</p>
                    </div>
                  </div>
                  <div className="review-note-card">
                    <FaCheckCircle />
                    <div>
                      <strong>Visible on the product page</strong>
                      <p>Your review helps other shoppers decide with confidence.</p>
                    </div>
                  </div>
                </div>

                <div className="review-form-actions">
                  <button type="button" className="review-secondary-button" onClick={() => navigate(`/order/${order._id}`)}>
                    Maybe later
                  </button>
                  <button type="submit" className="review-primary-button" disabled={submitting}>
                    {submitting ? "Submitting..." : "Submit review"}
                  </button>
                </div>
              </form>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default ReviewSubmissionPage;
